import { useCallback, useMemo } from 'react';

import { useGetLocationLabelRulesQuery } from '../redux/api/location';

/**
 * Display-only short location labels. Saved labels keep the
 * `Location.display_name` format ("T1 - Shelf 2 - A2"); this only changes what
 * people read and search:
 *
 * - slot / bin / heavy-rack locations drop the shelf: `T1-A2`
 * - unless the code repeats within the rack, which keeps it: `T3-Shelf 2-1`
 * - shared-shelf racks have no slot code, so they keep the shelf: `F1-Shelf 2`
 * - overflow / shop floor and anything unrecognised pass through.
 *
 * Mirrors `server/fastlane/location/labels.py`; keep the two in step.
 */

const SEPARATOR = ' - ';
const RACK_CODE = /^[A-Za-z0-9]{1,2}$/;
const STORAGE_TYPES = new Set([
	'SHARED_BINS',
	'INDIVIDUAL_SLOTS',
	'SHARED_SHELVES',
	'BUNDLE_SHARED_SHELF',
	'OVERFLOW',
	'SHOP_FLOOR',
	'HEAVY_RACK',
]);

export type LocationLabelFormatter = <T extends string | null | undefined>(text: T) => T | string;

const codeKey = (rack: string, code: string) => `${rack.trim().toUpperCase()}|${code.trim().toUpperCase()}`;

export function formatLocationLabel<T extends string | null | undefined>(
	text: T,
	ambiguous: ReadonlySet<string> = new Set(),
): T | string {
	if (typeof text !== 'string' || !text.includes(SEPARATOR)) return text;
	const parts = text.split(SEPARATOR).map((part) => part.trim());
	if ((parts.length !== 2 && parts.length !== 3) || !RACK_CODE.test(parts[0]) || !parts[1]) {
		return text;
	}
	const [rack, shelf, code] = parts;
	// Legacy manual-move metadata stored `str(location)` = "code - TYPE".
	if (STORAGE_TYPES.has(shelf.toUpperCase())) return text;
	if (parts.length === 2) return `${rack}-${shelf}`;
	if (!code) return text;
	if (ambiguous.has(codeKey(rack, code))) return `${rack}-${shelf}-${code}`;
	return `${rack}-${code}`;
}

/**
 * Text a picker filters on, so "T1-A2", "T1-Shelf 2-A2" and "T1 - Shelf 2 - A2"
 * all find the option, also before the rules load (search text is never shown).
 */
export const locationSearchText = (text: string | null | undefined, format: LocationLabelFormatter) =>
	text ? [...new Set([format(text), formatLocationLabel(text), text])].join(' ') : '';

/**
 * A saved label run through `format`, or a server short code only when there is
 * no saved label. Until the rules load `format` keeps the saved label, which is
 * the safe choice: a server short code like "T3-1" never keeps the shelf.
 */
export const labelOrShortCode = (
	displayName: string | null | undefined,
	shortCode: string | null | undefined,
	format: LocationLabelFormatter,
) => format(displayName) || shortCode;

// Other users and tabs change racks too: refresh the rules when a screen mounts
// on data older than 5 minutes, when the tab regains focus, and every 10 minutes
// (shop tablets stay on one screen for days).
const RULES_REFRESH = {
	refetchOnMountOrArgChange: 5 * 60,
	refetchOnFocus: true,
	pollingInterval: 10 * 60 * 1000,
} as const;

/**
 * Formatter bound to the server's repeated-code rules.
 * Until the rules load (or if they fail) labels stay unchanged, since shortening
 * without them could give two locations the same label.
 */
export function useLocationLabel(): LocationLabelFormatter {
	const { data } = useGetLocationLabelRulesQuery(undefined, RULES_REFRESH);
	const ambiguous = useMemo(() => (data ? new Set(data.ambiguous) : null), [data]);
	return useCallback(
		<T extends string | null | undefined>(text: T) =>
			ambiguous ? formatLocationLabel(text, ambiguous) : text,
		[ambiguous],
	);
}
